import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  Stack,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { styled } from '@mui/material/styles';
import { Button } from '@liminal/web-ui/primitives/Button';
import { useFFlags, useSetFFlagOverride } from '@/hooks/useFFlags';
import { UserPicker, type UserOption } from '@/features/users/components/UserPicker';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';
import { getErrorMessage } from '@/errors/messages';

const ActionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const FlagRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

interface FlagOverrideRow {
  id: string;
  flagName: string;
  enabled: boolean;
}

function newRow(): FlagOverrideRow {
  return {
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    flagName: '',
    enabled: true,
  };
}

export function NewFFlagOverridePage() {
  const navigate = useNavigate();
  const setMutation = useSetFFlagOverride();
  const { data: flagsData, isLoading: flagsLoading } = useFFlags();
  const flags = flagsData?.fflags ?? [];

  const [user, setUser] = useState<UserOption | null>(null);
  const [rows, setRows] = useState<FlagOverrideRow[]>(() => [newRow()]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateRow = (id: string, patch: Partial<FlagOverrideRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, newRow()]);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('Select a user');
      return;
    }
    if (rows.some((r) => !r.flagName)) {
      setError('Pick a flag for every row');
      return;
    }
    const seen = new Set<string>();
    for (const r of rows) {
      if (seen.has(r.flagName)) {
        setError(`Flag "${r.flagName}" is selected more than once`);
        return;
      }
      seen.add(r.flagName);
    }
    setError('');

    setSubmitting(true);
    try {
      await Promise.all(
        rows.map((r) =>
          setMutation.mutateAsync({ name: r.flagName, userId: user.userId, enabled: r.enabled }),
        ),
      );
      navigate('/fflags/users');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedFlagNames = new Set(rows.map((r) => r.flagName).filter(Boolean));

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="link"
          size="sm"
          startIcon="arrow-back"
          onClick={() => navigate('/fflags/users')}
        >
          Back to overrides
        </Button>
      </Stack>

      <PageHeader title="Add User Override" autoTitleCase={false} />
      <Card variant="elevated" padding="none">
        <Stack spacing={2.5} useFlexGap sx={{ p: 3 }}>
        <UserPicker value={user} onChange={setUser} required />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Flags
          </Typography>

          {rows.map((row) => (
            <FlagRow key={row.id}>
              <FormControl size="small" required sx={{ flex: 1 }}>
                <InputLabel>Flag</InputLabel>
                <Select
                  value={row.flagName}
                  label="Flag"
                  onChange={(e) => updateRow(row.id, { flagName: e.target.value })}
                  disabled={flagsLoading}
                >
                  {flags.map((f) => {
                    const takenElsewhere = selectedFlagNames.has(f.name) && f.name !== row.flagName;
                    return (
                      <MenuItem key={f.name} value={f.name} disabled={takenElsewhere}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontFamily: FONT_STACK_BODY }}>
                            {f.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            global: {f.enabled ? 'on' : 'off'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={row.enabled}
                    onChange={(e) => updateRow(row.id, { enabled: e.target.checked })}
                  />
                }
                label={row.enabled ? 'enabled' : 'disabled'}
                sx={{ minWidth: 110, mr: 0 }}
              />

              <IconButton
                icon="close"
                label="Remove flag"
                size="sm"
                tone="muted"
                onClick={() => removeRow(row.id)}
                disabled={rows.length <= 1}
              />
            </FlagRow>
          ))}

          <Box>
            <Button
              variant="link"
              size="sm"
              startIcon="plus"
              onClick={addRow}
              disabled={flagsLoading || rows.length >= flags.length}
            >
              Add another flag
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <ActionRow>
          <Button variant="link" onClick={() => navigate('/fflags/users')} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={submitting}>
            {rows.length > 1 ? `Save ${rows.length} Overrides` : 'Save Override'}
          </Button>
        </ActionRow>
        </Stack>
      </Card>
    </Box>
  );
}
